import { MastraClient } from "@mastra/client-js";
import type { StructuredDiffPayload } from "~/components/StructuredDiffViewer/StructuredDiffViewer";
import type { AgentResponse } from "../../interface/aiChat";
import type { GenerateKnowledge } from "../../schema/knowledge";
import { getStepNameFromId } from "../../utils/stepNameMappings";
import { ChatRequestApi } from "./chatRequest";

// 文字数制限の定数
const MAX_CHARACTERS = 4000;

// Mastra ストリームチャンクの payload 型
interface StreamChunkPayload {
  id?: string;
  toolName?: string;
  toolCallId?: string;
  result?: unknown;
  output?: unknown;
}

// ストリームコールバックに渡されるレコード型
export interface StreamRecord {
  type: string;
  code?: string;
  status?: string;
  message?: string;
  stepName?: string;
  toolName?: string;
  toolCallId?: string | undefined;
  toolResult?: {
    toolName: string;
    name?: string;
    toolCallId: string | undefined;
    id?: string;
    result: unknown;
  };
  updatedContent?: string;
  structuredDiff?: StructuredDiffPayload | null;
  result?: unknown;
  error?: unknown;
}

// Mastra SDK のスレッド型（SDK が詳細型を公開していないため最小定義）
export interface MastraThread {
  id: string;
  threadId?: string;
  title?: string;
  name?: string;
  updatedAt?: string;
  createdAt?: string;
}

// 文字数制限チェック関数
const validateCharacterLimit = (userInput: string): { isValid: boolean; error?: string } => {
  if (userInput.length > MAX_CHARACTERS) {
    return {
      isValid: false,
      error: `入力文字数が上限（${MAX_CHARACTERS}文字）を超えています。現在の文字数: ${userInput.length}文字`,
    };
  }
  return { isValid: true };
};

const getMastraBaseUrl = (): string => {
  if (!import.meta.env.VITE_MASTRA_BASE_URL) {
    throw new Error("VITE_MASTRA_BASE_URL is not set");
  }

  return import.meta.env.VITE_MASTRA_BASE_URL;
};

// エラーメッセージ抽出
const getErrorMessage = (error: unknown): string => {
  if (!error) return "不明なエラー";
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

// Azureのコンテンツフィルタに関する既知の文言を検出
const isAzureContentFilter = (message: string): boolean => {
  const m = message.toLowerCase();
  return (
    m.includes("content management policy") ||
    m.includes("content filter") ||
    m.includes("filtered due to the prompt") ||
    m.includes("azure openai")
  );
};

// ユーザー向けの日本語エラーメッセージへ変換
const toFriendlyMessage = (error: unknown): string => {
  const msg = getErrorMessage(error);
  if (isAzureContentFilter(msg)) {
    return "コンテンツポリシーによりブロックされました。禁止されている表現が含まれている可能性があります。入力内容を見直して再試行してください。";
  }
  return `エラーが発生しました: ${msg}`;
};

const detectCode = (error: unknown): "content-filter" | "stream-error" => {
  return isAzureContentFilter(getErrorMessage(error)) ? "content-filter" : "stream-error";
};

/**
 * Mastraストリームチャンクをフロントエンド用レコードに変換
 * streamWorkflow / watchWorkflow 共通のマッピング
 */
export const mapStreamChunkToRecord = (
  type: string,
  payload: StreamChunkPayload,
): StreamRecord | null => {
  if (type === "workflow-step-start") {
    if (!payload?.id) return null;
    const stepName = getStepNameFromId(payload.id);
    if (!stepName) return null;
    return {
      type: "step-start",
      stepName,
      message: `ステップ「${stepName}」を実行中...`,
      status: "in-progress",
    };
  }
  if (type === "workflow-step-result") {
    if (!payload?.id) return null;
    // editStep: editSkipped フラグに応じてステータスを切り替え
    if (payload.id === "edit") {
      const output = payload.output as { editSkipped?: boolean } | undefined;
      const skipped = output?.editSkipped === true;
      const stepName = getStepNameFromId(payload.id);
      if (!stepName) return null;
      return {
        type: "step-finish",
        stepName,
        message: `ステップ「${stepName}」が完了しました。`,
        status: skipped ? "skipped" : "completed",
      };
    }
    const stepName = getStepNameFromId(payload.id);
    if (!stepName) return null;
    return {
      type: "step-finish",
      stepName,
      message: `ステップ「${stepName}」が完了しました。`,
      status: "completed",
    };
  }
  if (type === "tool-call") {
    const toolName = payload?.toolName || "unknown-tool";
    return {
      type: "tool-call-display",
      toolName,
      toolCallId: payload?.toolCallId || payload?.id,
      message: `ツール「${toolName}」を実行中...`,
    };
  }
  if (type === "tool-result") {
    return {
      type: "tool-result",
      toolResult: {
        toolName: payload?.toolName || "unknown-tool",
        toolCallId: payload?.toolCallId || payload?.id,
        result: payload?.result,
      },
    };
  }
  return null;
};

export class AIChatApi {
  // バックエンドのMastraサーバーに接続するためのクライアント
  private mastraClient: MastraClient;
  private chatRequestApi: ChatRequestApi;

  constructor() {
    this.mastraClient = new MastraClient({
      baseUrl: getMastraBaseUrl(),
      credentials: "include",
      apiPrefix: "/chat/api",
    });
    this.chatRequestApi = new ChatRequestApi();
  }

  async streamWorkflow(
    agentId: string,
    userInput: string,
    documentId: string,
    projectId: string,
    threadId: string,
    resourceId: string,
    runId: string,
    editMode: boolean,
    onChunk?: (chunk: StreamRecord) => void,
    organizationId?: string,
    _userId?: string,
  ): Promise<AgentResponse | null> {
    try {
      // 文字数制限チェック
      const validation = validateCharacterLimit(userInput);
      if (!validation.isValid) {
        onChunk?.({
          type: "error",
          code: "character-limit",
          message: validation.error || "文字数制限エラー",
        });
        return {
          message: validation.error || "文字数制限エラー",
        };
      }

      let result: Record<string, unknown> | null = null;
      const workflow = this.mastraClient.getWorkflow(agentId);
      const run = await workflow.createRun({ runId });
      const stream = await run.stream({
        inputData: {
          userInput: userInput,
          documentId: documentId,
          projectId: projectId,
          threadId: threadId,
          resourceId: resourceId,
          runId: runId,
          editMode: editMode,
        },
      });

      for await (const chunk of stream) {
        const { type, payload } = chunk;
        if (type === "workflow-finish" || type === "finish") {
          // Mastra v1: workflow-finish / v0互換: finish
          const runState = await workflow.runById(runId);

          if (runState.status === "success") {
            const workflowResult = runState.result;
            result = workflowResult ?? null;
            onChunk?.({
              type: "workflow-finish",
              status: "completed",
              message: workflowResult?.["message"] ?? "ワークフローが正常に完了しました。",
              updatedContent: workflowResult?.["updatedContent"],
              structuredDiff:
                (workflowResult?.["structuredDiff"] as StructuredDiffPayload | null | undefined) ??
                null,
              result: workflowResult,
            });
          } else {
            onChunk?.({
              type: "workflow-error",
              status: "error",
              message: "ワークフローの実行中にエラーが発生しました。",
              error: runState,
            });
            onChunk?.({
              type: "error",
              code: detectCode(runState),
              message: toFriendlyMessage(runState),
            });

            // エラー時にクレジットを返却
            if (organizationId) {
              await this.chatRequestApi.failure(organizationId, projectId);
            }

            // ワークフロー失敗時はnullを返す（throwするとcatchで二重にクレジット返却される）
            return null;
          }
        } else {
          const record = mapStreamChunkToRecord(type, payload as StreamChunkPayload);
          if (record) onChunk?.(record);
        }
      }

      if (!result) return null;
      const response: AgentResponse = {
        message: (result["message"] as string) ?? "",
      };
      const updatedContent = result["updatedContent"];
      if (typeof updatedContent === "string") {
        response.updatedContent = updatedContent;
      }
      response.structuredDiff =
        (result["structuredDiff"] as StructuredDiffPayload | null | undefined) ?? null;
      return response;
    } catch (error) {
      console.error("Error calling streamAgentWorkflow with Mastra client:", error);
      // ストリーム異常終了時もUIへエラー通知を出し、ツールアイコンを error に更新させる

      try {
        onChunk?.({
          type: "error",
          code: detectCode(error),
          message: toFriendlyMessage(error),
        });
      } catch {}

      // エラー時にクレジットを返却
      if (organizationId) {
        await this.chatRequestApi.failure(organizationId, projectId);
      }

      return {
        message: "ワークフローの実行に失敗しました。",
      };
    }
  }

  // --- Workflow: get workflow by run ID ---
  async getWorkflow(runId: string): Promise<unknown> {
    try {
      const workflow = this.mastraClient.getWorkflow("agentWorkflow");
      const result = await workflow.runById(runId);
      return result;
    } catch (error) {
      console.error("AIChatApi.getWorkflow error:", error);
      return null;
    }
  }

  async watchWorkflow(
    agentId: string,
    runId: string,
    onRecord: (record: StreamRecord) => void,
  ): Promise<AgentResponse | null> {
    try {
      const workflow = this.mastraClient.getWorkflow(agentId);
      const run = await workflow.createRun({ runId });

      // TODO(REQ-2600): @mastra/client-js v1.2.0 の run.observeStream() は
      // 誤ったパス (/observe-stream) を使用し 404 になるため、
      // SDK の内部メソッドで正しいパス (/observe) にリクエストする
      const runInternal = run as unknown as {
        request: (path: string, options: { method: string; stream: boolean }) => Promise<Response>;
        createChunkTransformStream: () => TransformStream;
      };
      const response = await runInternal.request(`/workflows/${agentId}/observe?runId=${runId}`, {
        method: "POST",
        stream: true,
      });
      const stream = response.body!.pipeThrough(runInternal.createChunkTransformStream());

      for await (const chunk of stream as unknown as AsyncIterable<{
        type: string;
        payload: StreamChunkPayload;
      }>) {
        const { type, payload } = chunk;

        if (type === "workflow-finish" || type === "finish") {
          const runState = await workflow.runById(runId);
          if (runState.status === "success") {
            const workflowResult = runState.result;
            onRecord({
              type: "workflow-finish",
              status: "completed",
              message: workflowResult?.["message"] ?? "ワークフローが正常に完了しました。",
              updatedContent: workflowResult?.["updatedContent"],
              structuredDiff:
                (workflowResult?.["structuredDiff"] as StructuredDiffPayload | null | undefined) ??
                null,
              result: workflowResult,
            });
          } else {
            onRecord({
              type: "error",
              code: detectCode(runState),
              message: toFriendlyMessage(runState),
            });
          }
        } else {
          const record = mapStreamChunkToRecord(type, payload);
          if (record) onRecord(record);
        }
      }
      return null;
    } catch (error) {
      console.error("Error calling watchWorkflow with Mastra client:", error);
      return null;
    }
  }

  // --- Memory: create a thread by ID ---
  async createThread(agentId: string, resourceId: string, threadId: string): Promise<unknown> {
    try {
      const thread = await this.mastraClient.createMemoryThread({
        resourceId,
        agentId,
        threadId,
      });
      return thread;
    } catch (error) {
      console.error("AIChatApi.createThread error:", error);
      throw error;
    }
  }

  // --- Memory: list threads (max 10) ---
  async getThreads(
    agentId: string,
    resourceId: string,
    limit: number = 10,
  ): Promise<MastraThread[]> {
    try {
      const response = await this.mastraClient.listMemoryThreads({
        resourceId,
      });
      const arr = Array.isArray(response.threads)
        ? (response.threads as unknown as MastraThread[])
        : [];
      // updatedAtで降順ソート
      const sorted = arr.sort((a, b) => {
        const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return bTime - aTime;
      });

      // 先頭30件のみ保存対象、31件目以降は削除
      const keepThreads = sorted.slice(0, 30);
      const deleteThreads = sorted.slice(30);

      // 31件目以降を並列削除（Promise.allSettledで失敗を許容）
      if (deleteThreads.length > 0) {
        const deletePromises = deleteThreads.map(async (thread) => {
          if (thread.id) {
            await this.deleteThread(agentId, thread.id);
          }
        });

        const results = await Promise.allSettled(deletePromises);
        results.forEach((result, index) => {
          if (result.status === "rejected") {
            console.error(`Thread deletion failed for ${deleteThreads[index]?.id}:`, result.reason);
          }
        });
      }

      // 表示名の正規化: "New Thread" で始まる場合は日本語に置換
      const normalized = keepThreads.slice(0, limit).map((t) => {
        const original = t.title ?? t.name ?? "";
        const isDefaultEn = typeof original === "string" && original.startsWith("New Thread");
        const title = isDefaultEn ? "新しいチャット" : original;
        return { ...t, title };
      });
      return normalized;
    } catch (error) {
      console.error("AIChatApi.getThreads error:", error);
      return [];
    }
  }

  // --- Memory: get thread by ID ---
  async getThread(agentId: string, threadId: string): Promise<unknown> {
    try {
      const thread = this.mastraClient.getMemoryThread({ threadId, agentId });
      const data = await thread.get();
      return data;
    } catch (error) {
      console.error("AIChatApi.getThread error:", error);
      return null;
    }
  }

  // --- Memory: delete a thread by ID ---
  async deleteThread(agentId: string, threadId: string): Promise<boolean> {
    try {
      const thread = this.mastraClient.getMemoryThread({ threadId, agentId });
      await thread.delete();
      return true;
    } catch (error) {
      console.error("AIChatApi.deleteThread error:", error);
      return false;
    }
  }

  // --- Memory: get messages in a thread (limit default 30) ---
  async getMessages(agentId: string, threadId: string, limit: number = 30): Promise<object[]> {
    try {
      const thread = this.mastraClient.getMemoryThread({ threadId, agentId });
      if (!thread) {
        console.warn("[AIChatApi.getThreadMessages] thread not found");
        throw new Error("Thread not found");
      }

      const result = await thread.listMessages({ perPage: limit });
      const messages = result.messages;
      return messages;
    } catch (error) {
      console.error("AIChatApi.getThreadMessages error:", error);
      return [];
    }
  }

  // --- Message: create a message by thread ID ---
  async createMessage(
    message: string,
    knowledgeId: string,
    threadId: string,
    resourceId: string,
  ): Promise<boolean> {
    try {
      const date = new Date();
      const messageId = `${threadId}_${date.toISOString()}`;

      await this.mastraClient.saveMessageToMemory({
        messages: [
          {
            id: messageId,
            role: "assistant" as const, // metadata.messageTypeでシステムメッセージを識別
            createdAt: date,
            threadId: threadId,
            resourceId: resourceId,
            type: "text",
            content: {
              format: 2,
              parts: [
                {
                  type: "text",
                  text: message,
                },
              ],
              content: message,
              metadata: {
                knowledgeId: knowledgeId,
                messageType: "system", // システムメッセージの識別子
                createdAt: date.toISOString(),
              },
            },
          },
        ],
        agentId: "commonAgent",
      });

      return true;
    } catch (error) {
      console.error("AIChatApi.createMessage error:", error);
      return false;
    }
  }

  // --- Knowledge: create knowledge by thread ID ---
  async createKnowledge(threadId: string, limit: number = 10): Promise<GenerateKnowledge> {
    try {
      const agentId = "commonAgent";
      const thread = this.mastraClient.getMemoryThread({ threadId, agentId });

      const messageResponse = await thread.listMessages({ perPage: limit });
      if (!messageResponse) {
        console.warn("[AIChatApi.getKnowledge] messages not found");
        throw new Error("Messages not found");
      }

      const data = {
        messages: messageResponse.messages.map((m: { role: string; content: unknown }) => ({
          role: m.role,
          content: JSON.stringify(m.content),
        })),
      };

      const tool = this.mastraClient.getTool("knowledgeAgentTool");
      const response = await tool.execute({
        data: {
          messages: data.messages,
        },
      });

      const knowledge = response as GenerateKnowledge;
      if (!knowledge) {
        console.warn("[AIChatApi.getKnowledge] knowledge not found");
        throw new Error("Knowledge not found");
      }
      return knowledge;
    } catch (error) {
      console.error("AIChatApi.getKnowledge error:", error);
      throw error;
    }
  }
}
